import { lazy, Suspense } from 'react';
import { Route, Routes } from 'react-router';
import 'src/tailwind.css';
import 'allotment/dist/style.css';
import 'react-toastify/dist/ReactToastify.css';
import * as Sentry from '@sentry/react';
import AuthenticatedApp from './components/AuthenticatedApp';
import { RuleManagement } from './components/admin/RuleManagement';

const Dashboard = lazy(() => import('src/pages/Dashboard.tsx'));
const Income = lazy(() => import('src/pages/loan/Income'));
const IncomeAnalyzer = lazy(() => import('src/pages/loan/IncomeAnalyzer'));
const AdminTasks = lazy(() => import('src/pages/admin/AdminTasks'));
const LoanDashboard = lazy(() => import('src/pages/LoanDashboard.tsx'));
const ChecklistDashboard = lazy(
  () => import('src/pages/loan/checklist/ChecklistDashboard.tsx'),
);
const ManageChecklist = lazy(
  () => import('src/pages/loan/checklist/ManageChecklist'),
);
const RuleBuilder = lazy(() => import('src/pages/loan/checklist/RuleBuilder'));
const Extraction = lazy(() => import('src/pages/loan/Extraction.tsx'));
const Panel = lazy(() => import('src/pages/loan/Panel.tsx'));
const Tasks = lazy(() => import('src/pages/loan/Tasks.tsx'));

const AdminSettings = lazy(() => import('src/layout/AdminSettings'));
const AdminMosConfiguration = lazy(
  () => import('src/pages/admin/AdminMosConfiguration.tsx'),
);

const AdminNotification = lazy(
  () => import('src/pages/admin/AdminNotification.tsx'),
);
const DataPanel = lazy(() => import('src/pages/loan/DataPanel.tsx'));
const AdminTenants = lazy(() => import('src/pages/admin/AdminTenants.tsx'));
const AdminLoans = lazy(() => import('src/pages/admin/AdminLoans'));
const AdminLoanTypes = lazy(() => import('src/pages/admin/AdminLoanTypes.tsx'));
const AdminSentry = lazy(() => import('src/pages/admin/AdminSentry'));
const AdminProfile = lazy(() => import('src/pages/admin/AdminProfile'));
const AdminNotificationSettings = lazy(
  () => import('src/pages/admin/AdminNotificationSettings'),
);
const AdminInvites = lazy(() => import('src/pages/admin/AdminInvites'));
const AdminDocumentGrouping = lazy(
  () => import('src/pages/admin/AdminDocumentGrouping'),
);
const AdminDocumentGroupingEdit = lazy(
  () => import('src/pages/admin/AdminDocumentGroupingEdit'),
);
const AdminIncomeGroupingEdit = lazy(
  () => import('src/pages/admin/AdminIncomeGroupingEdit'),
);
const Notification = lazy(
  () => import('src/pages/notification/Notification.tsx'),
);
const AdminUser = lazy(() => import('src/pages/admin/AdminUser.tsx'));
const AdminMappings = lazy(() => import('src/pages/admin/AdminMappings.tsx'));
const AdminDocumentTypes = lazy(
  () => import('src/pages/admin/AdminDocumentTypes.tsx'),
);
const AdminRoleManagement = lazy(
  () => import('src/pages/admin/AdminRoleManagement.tsx'),
);
const AdminRoleEdit = lazy(() => import('src/pages/admin/AdminRoleEdit'));
const AdminUserSessionManagement = lazy(
  () => import('src/pages/admin/AdminUserSessionManagement.tsx'),
);
const AdminDocumentTypeManagement = lazy(
  () => import('src/pages/admin/AdminDocumentTypeManagement.tsx'),
);
const AdminApiKeys = lazy(() => import('src/pages/admin/AdminApiKeys.tsx'));
const AdminIntegrations = lazy(
  () => import('src/pages/admin/AdminIntegrations.tsx'),
);
const AcceptInvitation = lazy(
  () => import('src/pages/invites/AcceptInvitation'),
);
const MagicLinkCallback = lazy(
  () => import('src/pages/auth/MagicLinkCallback'),
);
const MagicLinkCodeEntry = lazy(
  () => import('src/pages/auth/MagicLinkCodeEntry'),
);
const AdminChecklistManagement = lazy(
  () => import('src/pages/admin/AdminChecklistManagement.tsx'),
);
const NotFound404 = lazy(() => import('src/pages/NotFound404'));
const UnauthorizedAccess = lazy(() => import('src/pages/UnauthorizedAccess'));

const SentryRoutes = Sentry.withSentryReactRouterV7Routing(Routes);

const App = () => (
  <Suspense fallback={null}>
    <SentryRoutes>
      <Route path="/accept-invite" element={<AcceptInvitation />} />
      <Route path="/magic-link/callback" element={<MagicLinkCallback />} />
      <Route path="/magic-link/enter-code" element={<MagicLinkCodeEntry />} />
      <Route path="/unauthorized" element={<UnauthorizedAccess />} />
      <Route path="/" element={<AuthenticatedApp />}>
        <Route index element={<Dashboard />} />
        <Route path="/loan/:loanId">
          <Route index element={<ChecklistDashboard />} />
          <Route path="dashboard" element={<LoanDashboard />} />
          <Route path="panel" element={<Panel />} />
          <Route path="extraction/:documentId" element={<Extraction />} />
          <Route path="checklist" element={<ManageChecklist />} />
          <Route path="/loan/:loanId/task" element={<Tasks />} />
          <Route path="checklist/:checklistId" element={<ManageChecklist />} />
          <Route path="data-panel" element={<DataPanel />} />
          <Route
            path="checklist/:checklistId/rule/:ruleId"
            element={<RuleBuilder />}
          />
          <Route path="/loan/:loanId/income" element={<Income />} />
          <Route path="income-analyzer" element={<IncomeAnalyzer />} />
        </Route>
        <Route path="/notification" element={<Notification />} />
        <Route path="/admin" element={<AdminSettings />}>
          <Route index element={<AdminProfile />} />
          <Route
            path="notification-preferences"
            element={<AdminNotificationSettings />}
          />
          <Route
            path="checklist-management"
            element={<AdminChecklistManagement />}
          />
          <Route
            path="checklist-management/:checklistId"
            element={<RuleManagement />}
          />
          <Route path="configuration" element={<AdminMosConfiguration />} />
          <Route path="notifications" element={<AdminNotification />} />
          <Route path="mos-api" element={<AdminMosConfiguration />} />
          <Route path="api-keys" element={<AdminApiKeys />} />
          <Route path="integrations" element={<AdminIntegrations />} />
          <Route path="tenants" element={<AdminTenants />} />
          <Route path="loans" element={<AdminLoans />} />
          <Route path="loan-types" element={<AdminLoanTypes />} />
          <Route path="sentry" element={<AdminSentry />} />
          <Route path="invites" element={<AdminInvites />} />
          <Route path="tasks" element={<AdminTasks />} />
          <Route path="users" element={<AdminUser />} />
          <Route path="mappings" element={<AdminMappings />} />
          <Route path="document-types" element={<AdminDocumentTypes />} />
          <Route path="roles" element={<AdminRoleManagement />} />
          <Route path="roles/:id" element={<AdminRoleEdit />} />
          <Route
            path="session-management"
            element={<AdminUserSessionManagement />}
          />
          <Route
            path="document-types/:documentTypeId"
            element={<AdminDocumentTypeManagement />}
          />
          <Route path="doc-group" element={<AdminDocumentGrouping />} />
          <Route
            path="doc-group/income/:id"
            element={<AdminIncomeGroupingEdit />}
          />
          <Route path="doc-group/:id" element={<AdminDocumentGroupingEdit />} />
        </Route>
        <Route path="*" element={<NotFound404 />} />
      </Route>
    </SentryRoutes>
  </Suspense>
);

export default App;
